/**
 * Axios client for the UAVSimNet operator API.
 *
 * Phase 6: replaces the legacy ``X-API-Token`` interceptor with a Bearer
 * JWT pulled from ``localStorage["uavsimnet_jwt"]``. On HTTP 401 the token
 * is purged and the SPA navigates to ``/login``. A small proactive refresh
 * helper bumps the token before it expires whenever the operator has been
 * recently active.
 */
import axios from "axios";

const TOKEN_KEY = "uavsimnet_jwt";
const EXPIRES_AT_KEY = "uavsimnet_jwt_expires_at";
const LAST_ACTIVITY_KEY = "uavsimnet_last_activity";

const REFRESH_LEEWAY_MS = 5 * 60 * 1000;        // refresh if <5 min remaining
const RECENT_ACTIVITY_WINDOW_MS = 10 * 60 * 1000; // … and active within 10 min

const BASE_URL = process.env.REACT_APP_BACKEND_URL;
if (!BASE_URL) {
    // Surface a clear error early in the console — every API call would
    // otherwise resolve against the SPA host and 404.
    console.warn("[uavsimnet] REACT_APP_BACKEND_URL is not set; API calls will fail.");
}

export function getStoredToken() {
    return localStorage.getItem(TOKEN_KEY) || "";
}

export function setStoredToken(token, expiresAt) {
    if (token) {
        localStorage.setItem(TOKEN_KEY, token);
        if (expiresAt) localStorage.setItem(EXPIRES_AT_KEY, expiresAt);
    } else {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(EXPIRES_AT_KEY);
    }
}

export function getStoredExpiresAt() {
    const v = localStorage.getItem(EXPIRES_AT_KEY) || "";
    return v ? Date.parse(v) : 0;
}

export function clearStoredAuth() {
    setStoredToken("", "");
}

/* --------------------------------------------------------------------- */
/* Activity tracking — used by the proactive refresh helper.              */
/* --------------------------------------------------------------------- */
export function markUserActivity() {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
}

function lastActivityAge() {
    const v = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || "0", 10);
    return v ? Date.now() - v : Infinity;
}

/* --------------------------------------------------------------------- */
/* Axios instance                                                         */
/* --------------------------------------------------------------------- */
export const api = axios.create({
    baseURL: BASE_URL,
    headers: { "Content-Type": "application/json" },
    timeout: 60000,
});

api.interceptors.request.use((config) => {
    const token = getStoredToken();
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    // Defensive: scrub any X-API-Token that may have been set by stale code.
    if (config.headers && "X-API-Token" in config.headers) {
        delete config.headers["X-API-Token"];
    }
    return config;
});

let _onUnauthorized = null;
let _onPaymentRequired = null;
export function setUnauthorizedHandler(fn) {
    _onUnauthorized = typeof fn === "function" ? fn : null;
}
export function setPaymentRequiredHandler(fn) {
    _onPaymentRequired = typeof fn === "function" ? fn : null;
}

api.interceptors.response.use(
    (resp) => resp,
    async (error) => {
        const status = error?.response?.status;
        const url = (error?.config?.url || "").toString();
        // Don't bounce on /api/auth/login — that's an expected 401 path.
        if (status === 401 && !url.includes("/api/auth/login")) {
            clearStoredAuth();
            if (_onUnauthorized) _onUnauthorized();
        }
        // Phase 16 — subscription gate. The backend returns 402 with
        // {"error": "subscription_required"} when an authenticated tenant
        // has no active subscription. Hand off to the registered handler
        // so AuthContext can route to /subscription-required.
        if (status === 402 && _onPaymentRequired) {
            const detail = error?.response?.data?.detail || error?.response?.data;
            if (detail && (detail.error === "subscription_required" ||
                (typeof detail === "object" && detail.contact))) {
                _onPaymentRequired(detail);
            }
        }
        return Promise.reject(error);
    },
);

/* --------------------------------------------------------------------- */
/* Proactive refresh helper                                               */
/*                                                                        */
/*  Call this on a timer / route change. If the token will expire in less */
/*  than REFRESH_LEEWAY_MS and the operator was active within the last    */
/*  RECENT_ACTIVITY_WINDOW_MS, request a fresh JWT.                       */
/* --------------------------------------------------------------------- */
let _refreshing = null;

export async function maybeRefreshToken() {
    const token = getStoredToken();
    if (!token) return;
    const expiresAt = getStoredExpiresAt();
    if (!expiresAt) return;

    const remaining = expiresAt - Date.now();
    if (remaining > REFRESH_LEEWAY_MS) return;
    if (lastActivityAge() > RECENT_ACTIVITY_WINDOW_MS) return;

    if (_refreshing) return _refreshing;
    _refreshing = (async () => {
        try {
            const resp = await api.post("/api/auth/refresh");
            const data = resp.data || {};
            if (data.access_token) {
                setStoredToken(data.access_token, data.expires_at);
                return data;
            }
        } catch (_e) {
            // Best effort — leave the existing token in place. The next
            // 401 will force a normal re-login.
        } finally {
            _refreshing = null;
        }
        return null;
    })();
    return _refreshing;
}

/* --------------------------------------------------------------------- */
/* Backward-compat exports (kept so legacy pages compile unchanged).      */
/* --------------------------------------------------------------------- */
export const getToken = getStoredToken;
export const setToken = (t) => setStoredToken(t, "");
export const clearToken = clearStoredAuth;

/** Lift a server detail string out of any Axios error. */
export function serverDetail(err) {
    const d = err?.response?.data;
    if (!d) return err?.message || "Network error";
    if (typeof d === "string") return d;
    if (typeof d.detail === "string") return d.detail;
    if (Array.isArray(d.detail)) {
        return d.detail.map((e) => `${(e.loc || []).join(".")} ${e.msg}`).join("; ");
    }
    if (d.detail && typeof d.detail === "object") return JSON.stringify(d.detail);
    return JSON.stringify(d);
}

// Default export = axios instance (legacy code did `import client from "@/api/client"`).
export default api;
